import React, { useEffect, useRef, useState } from "react";

import LocalPrintshopOutlinedIcon from "@mui/icons-material/LocalPrintshopOutlined";
import { Box, Grid, Typography } from "@mui/material";
import { Form, Formik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import useSWR from "swr";

import { getLastPetsByWorkspaceId, uploadS3Bucket } from "../../Api/axios";
import {
  createNewPet,
  getPetByID,
  updatePetImageById,
  updatePetInfoById,
} from "../../Api/Services/PetServices";
import { MEMBERSHIP_NUMBER_KEY, WORKSPACEID_KEY } from "../../Constant";
import useAuth from "../../ContextProvider/AuthProvider";
import {
  newSmallFieldBoxStyle,
  Column,
  Body,
  FormHeaderBox,
  FormHeaderTitle,
  CancelButton,
  ButtonBoxStyle,
} from "../../Css";
import { PetInfoFormValidation } from "../../From/Validation";
import {
  chipOptionsCustom,
  genderOptionsCustom,
  eyeColorOptions,
  petTypeOptions,
  weightMeasurementOptions,
  furColorOptions,
} from "../../Options";
import { formatDate, getCountryAsOption, getStateAsOptions } from "../../Utils";
import AutoComplete from "../Common/AutoComplete";
import AutoCompleteAddress from "../Common/AutoCompleteAddress";
import CustomButton from "../Common/Button";
import FormGroup from "../Common/FormGroup";
import FormLayout from "../Common/FormHeader";
import ImageUpload from "../Common/ImageUpload";
import InputField from "../Common/InputField";
import CustomPhoneInput from "../Common/PhoneInput";
import { showSnackbar } from "../Common/Snackbar";
import "../Item-List/GlobalItemList.css";
import "react-phone-input-2/lib/style.css";
import "./petPrint.css";
import { useTheme } from "@mui/material/styles";

const defaultValue = {
  user_id: "",
  name: "",
  family_name: "",
  dob: "",
  breed: "",
  company_name: "",
  company_contact_number: "",
  marks: "",
  reg_number: "",
  chip_availability: "",
  fur_color: "",
  eye_Color: "",
  gender: "",
  weight: "",
  weightMeasurement: "",
  pet_type: "",
  zip_code: "",
  street_address: "",
  apt_city: "",
  country: "",
  state: "",
  membership_no: "",
  membership_id: "",
};

export default function PetInfoForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const theme = useTheme();
  const componentPDF = useRef();
  
  const [initialValue, setInitialValue] = useState(defaultValue);
  const [isUpdating, setIsUpdating] = useState(false);
  const [open, setOpen] = useState(false);
  const [countryOptions, setCountryOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);
  const [membershipNo, setMembershipNo] = useState("");
  const [image, setImage] = useState(null);
  
  const formikRef = useRef();
  const maxDate = new Date().toISOString().substr(0, 10);

  const { petOwnerId: user_id } = useAuth();
  const membership_id = localStorage.getItem(MEMBERSHIP_NUMBER_KEY);
  const w_id = localStorage.getItem(WORKSPACEID_KEY);

  const isEditMode = Boolean(id);

  // Fetch pet data for editing
  const { data: petInfo = {} } = useSWR(
    isEditMode ? `petData${id}` : null,
    async () => {
      try {
        const { data } = await getPetByID(id);
        const petData = data?.data;
        setImage(petData.image);
        
        const weightArray = petData.weight.match(/^([\d.]+)(\D+)$/);
        if (weightArray && weightArray.length > 2) {
          setInitialValue({
            ...petData,
            weight: weightArray[1],
            weightMeasurement: weightArray[2],
            membership_id,
            dob: formatDate(petData.dob_date),
          });
        } else {
          setInitialValue({
            ...petData,
            membership_id,
            dob: formatDate(petData.dob),
          });
        }
        
        const country = getCountryAsOption();
        setCountryOptions(country);
        const countryCode = country?.find(
          (country) => country.label === petData?.country
        );
        const state = getStateAsOptions(countryCode?.isoCode);
        const filteredState = state?.map((item) => {
          return {
            value: item.label,
            label: item.label,
          };
        });
        filteredState.push({ value: "Other", label: "Other" });
        setStateOptions(filteredState);
        return data?.data;
      } catch (e) {
        console.error(e);
        return null;
      }
    },
    {
      revalidateOnFocus: false,
    }
  );

  const setNextMemberId = async () => {
    try {
      const { data } = await getLastPetsByWorkspaceId(w_id);
      let membership_no = `${membership_id}-0-001`;
      let maxMembershipNo = 1;
      if (data?.data?.length) {
        const pet = data.data[0];
        const petMembershipNo = pet.membership_no;
        if (petMembershipNo) {
          const [, , , , , , number] = petMembershipNo.split("-");
          const parsedNumber = parseInt(number, 10);
          maxMembershipNo = Math.max(maxMembershipNo, parsedNumber);
          const nextNumber = (maxMembershipNo + 1).toString().padStart(3, "0");
          membership_no = `${membership_id}-0-${nextNumber}`;
        }
      }
      setMembershipNo(membership_no);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (!isEditMode) {
      setNextMemberId();
    }
  }, [membership_id, user_id, isEditMode]);

  useEffect(() => {
    const country = getCountryAsOption();
    setCountryOptions(country);
  }, []);

  const createPetData = async (values) => {
    const value = {
        ...values,
      weight: values?.weight + values?.weightMeasurement,
      membership_no: membershipNo,
      w_id: w_id,
    };
    
    try {
      const res = await createNewPet(value);
      if (image instanceof File) {
        await uploadPetImage(res.data.pet_details.id, res.data.history_id);
      }
      return res;
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  const updatePetData = async (values) => {
    try {
      const value = {
        ...values,
        dob: new Date(values.dob),
        company_contact_number: values.company_contact_number,
        weight: values.weight + values.weightMeasurement,
      };
      
      const res = await updatePetInfoById(id, value);
      if (image instanceof File) {
        await uploadPetImage(id, res?.data?.history_id);
      }
      return res;
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  const uploadPetImage = async (petId, historyId) => {
    try {
      const updateData = {
        history_id: historyId,
        type: image?.type,
        file_name: `${Date.now()}-${image?.name}`,
      };
      const res = await updatePetImageById(petId, updateData);
      const url = res?.data?.data;
      const decodedUrl = decodeURIComponent(url);
      const uploadRes = await uploadS3Bucket(decodedUrl, image);
      if (uploadRes?.status === 200) {
        console.log("Image uploaded successfully", uploadRes);
      }
    } catch (error) {
      console.error("Image upload failed:", error);
    }
  };

  const handleSubmit = async (values) => {
    setIsUpdating(true);
    try {
      if (isEditMode) {
        await updatePetData(values);
        showSnackbar({
          type: "success",
          message: "Pet information updated successfully.",
        });
      } else {
        await createPetData(values);
        showSnackbar({
          type: "success",
          message: "Pet information added successfully.",
        });
      }
      setOpen(true);
    } catch (err) {
      showSnackbar({
        type: "error",
        message: isEditMode ? "Pet information update failed." : "Pet information add failed.",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCountryChange = (e, newValue) => {
    const state = getStateAsOptions(newValue?.isoCode);
    const filteredState = state?.map((item) => {
      return {
        value: item.label,
        label: item.label,
      };
    });
    filteredState.push({ value: "Other", label: "Other" });
    setStateOptions(filteredState);
    formikRef?.current?.setFieldValue("state", "");
  };

  const handlePrint = useReactToPrint({
    content: () => componentPDF.current,
    documentTitle: "Pet Information",
    pageStyle: `
      @page {
        size: A3;
        margin: 200px;
      }
      body {
        margin: 100px;
        padding: 10px;
      }
    `,
    printStyle: `
      .print-text-field {
        font-size: 14px;
        padding: 10px;
        margin-bottom: 20px;
      }
    `,
  });

  return (
    <FormLayout title={isEditMode ? "Edit Pet Information" : "Pet Information"}>
      <Formik
        initialValues={initialValue}
        innerRef={formikRef}
        onSubmit={handleSubmit}
        validationSchema={PetInfoFormValidation}
        validateOnMount={false}
        enableReinitialize
      >
        {({ getFieldProps, values, setFieldValue, isSubmitting }) => (
          <Form>
            <Box sx={FormHeaderBox(theme.palette.primary.main)}>
              <Box
                sx={{
                  borderBottom: `1px solid ${theme.palette.primary.main}`,
                  width: "100%",
                }}
              >
                <Typography variant="h6" sx={FormHeaderTitle}>
                  Pet Details
                </Typography>
              </Box>
              <div className="scroll2" ref={componentPDF} style={{ width: "100%" }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "flex-start",
                    justifyContent: "center",
                  }}
                >
                  <ImageUpload
                    alt="Uploaded_Image"
                    id="imageInput"
                    image={image}
                    setImage={setImage}
                    accept="image/*"
                    boxSx={{
                      width: {
                        sm: 120,
                        md: 120,
                        xs: 93,
                        lg: 130,
                        xl: 130,
                      },
                      height: {
                        sm: 120,
                        md: 120,
                        xs: 105,
                        lg: 130,
                        xl: 130,
                      },
                    }}
                  />
                </Box>
                <Body style={{ gap: "10px 20px" }}>
                  <Column>
                    <FormGroup name="pet_type" label={"Pet Type"} requiredField>
                      <AutoComplete
                        {...getFieldProps("pet_type")}
                        name="pet_type"
                        options={petTypeOptions}
                        placeholder="Select a type"
                        otherType="Other"
                      />
                    </FormGroup>
                  </Column>
                  <Column>
                    <FormGroup name="name" label={"Pet Name"} requiredField>
                      <InputField {...getFieldProps("name")} />
                    </FormGroup>
                  </Column>
                  <Column>
                    <FormGroup name="breed" label={"Breed"} requiredField>
                      <InputField {...getFieldProps("breed")} />
                    </FormGroup>
                  </Column>
                  <Column>
                    <FormGroup name="marks" label={"Marks"}>
                      <InputField {...getFieldProps("marks")} />
                    </FormGroup>
                  </Column>
                  <Column>
                    <FormGroup
                      name="family_name"
                      label={"Family Name"}
                      requiredField
                    >
                      <InputField {...getFieldProps("family_name")} />
                    </FormGroup>
                  </Column>
                  {isEditMode && (
                    <Column>
                      <Box sx={{ ...newSmallFieldBoxStyle, paddingBottom: 0 }}>
                        <Box style={{ width: "50%" }}>
                          <FormGroup name="membership_id" label={"Membership Id"}>
                            <InputField
                              {...getFieldProps("membership_id")}
                              disabled
                            />
                          </FormGroup>
                        </Box>
                        <Box style={{ paddingLeft: "5px", width: "50%" }}>
                          <FormGroup name="membership_no" label={"Membership No"}>
                            <InputField
                              {...getFieldProps("membership_no")}
                              disabled
                            />
                          </FormGroup>
                        </Box>
                      </Box>
                    </Column>
                  )}
                  <Column>
                    <FormGroup name="street_address" label={"Street Address"}>
                      <AutoCompleteAddress
                        formikRef={formikRef}
                        setFieldValue={setFieldValue}
                        value={values?.street_address}
                        address="street_address"
                        city="apt_city"
                        state="state"
                        zipcode="zip_code"
                        country="country"
                      />
                    </FormGroup>
                  </Column>
                  <Column>
                    <FormGroup name="dob" label={"Date of Birth"} requiredField>
                      <InputField
                        type="date"
                        className="Date"
                        inputProps={{ max: maxDate }}
                        {...getFieldProps("dob")}
                      />
                    </FormGroup>
                  </Column>
                  <Column>
                    <Grid container spacing={1}>
                      <Grid item xs={12} sm={3}>
                        <FormGroup name="weight" label={"Weight"} requiredField>
                          <InputField
                            type="number"
                            {...getFieldProps("weight")}
                          />
                        </FormGroup>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <FormGroup
                          name="weightMeasurement"
                          label={"Measure"}
                          requiredField
                        >
                          <AutoComplete
                            {...getFieldProps("weightMeasurement")}
                            name="weightMeasurement"
                            options={weightMeasurementOptions}
                            placeholder="Select a Measure"
                          />
                        </FormGroup>
                      </Grid>
                      <Grid item xs={12} sm={5}>
                        <FormGroup name="gender" label={"Gender"} requiredField>
                          <AutoComplete
                            {...getFieldProps("gender")}
                            name="gender"
                            options={genderOptionsCustom}
                            placeholder="Select a gender"
                          />
                        </FormGroup>
                      </Grid>
                    </Grid>
                  </Column>
                  <Column>
                    <FormGroup name="country" label={"Country"}>
                      <AutoComplete
                        {...getFieldProps("country")}
                        name="country"
                        options={countryOptions}
                        placeholder="Select a country"
                        onChange={handleCountryChange}
                        isOnChange
                      />
                    </FormGroup>
                  </Column>
                  <Column>
                    <Grid container spacing={1}>
                      <Grid item xs={12} sm={6}>
                        <FormGroup name="state" label={"State"}>
                          <AutoComplete
                            {...getFieldProps("state")}
                            name="state"
                            otherType="Other"
                            options={stateOptions}
                            placeholder={"Select a State"}
                          />
                        </FormGroup>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormGroup name="zip_code" label={"Zip Code"}>
                          <InputField
                            {...getFieldProps("zip_code")}
                            name="zip_code"
                          />
                        </FormGroup>
                      </Grid>
                    </Grid>
                  </Column>
                  <Column>
                    <Grid container spacing={1}>
                      <Grid item xs={12} sm={6}>
                        <FormGroup
                          name="fur_color"
                          label={"Color"}
                          requiredField
                        >
                          <AutoComplete
                            {...getFieldProps("fur_color")}
                            name="fur_color"
                            options={furColorOptions}
                            placeholder={"Select a color"}
                            otherType="Other"
                          />
                        </FormGroup>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormGroup
                          name="eye_Color"
                          label={"Eye Color"}
                          requiredField
                        >
                          <AutoComplete
                            {...getFieldProps("eye_Color")}
                            name="eye_Color"
                            options={eyeColorOptions}
                            otherType="Other"
                            placeholder={"Select a color"}
                          />
                        </FormGroup>
                      </Grid>
                    </Grid>
                  </Column>
                  <Column>
                    <FormGroup name="apt_city" label={"City"}>
                      <InputField {...getFieldProps("apt_city")} />
                    </FormGroup>
                  </Column>
                  <Column>
                    <Box sx={{ ...newSmallFieldBoxStyle, paddingBottom: "0px" }}>
                      <Box sx={{ width: { sm: '50%', xs: '100%' } }}>
                        <FormGroup
                          name="chip_availability"
                          label={"Micro Chip Availability"}
                        >
                          <AutoComplete
                            {...getFieldProps("chip_availability")}
                            name="chip_availability"
                            options={chipOptionsCustom}
                            placeholder="Select a Micro Chip Availability"
                          />
                        </FormGroup>
                      </Box>
                      {values.chip_availability === "Yes" && (
                        <Box sx={{ width: { sm: '50%', xs: '100%', paddingLeft: "5px" } }}>
                          <FormGroup
                            name="reg_number"
                            label={"Register Number"}
                          >
                            <InputField
                              {...getFieldProps("reg_number")}
                              name="reg_number"
                            />
                          </FormGroup>
                        </Box>
                      )}
                    </Box>
                  </Column>
                  {values.chip_availability === "Yes" && (
                    <Column>
                      <Box sx={{ ...newSmallFieldBoxStyle, paddingBottom: "0px" }}>
                        <Box sx={{ width: { sm: '50%', xs: '100%' } }}>
                          <FormGroup name="company_name" label={"Company Name"}>
                            <InputField
                              {...getFieldProps("company_name")}
                              name="company_name"
                            />
                          </FormGroup>
                        </Box>
                        <Box sx={{ width: { sm: '50%', xs: '100%' }, paddingLeft: '10px' }}>
                          <FormGroup
                            name="company_contact_number"
                            label={"Company Phone Number"}
                          >
                            <CustomPhoneInput
                              {...getFieldProps("company_contact_number")}
                            />
                          </FormGroup>
                        </Box>
                      </Box>
                    </Column>
                  )}
                </Body>
              </div>
              <Box sx={ButtonBoxStyle(`${theme.palette.primary.main}`)}>
                <Box display="flex">
                  <CustomButton
                    label="Cancel"
                    type="button"
                    style={CancelButton(`${theme.palette.primary.main}`)}
                    onClick={() => navigate(-1)}
                  />
                  <CustomButton
                    label={isSubmitting || isUpdating ? (isEditMode ? "Saving..." : "Submitting...") : (isEditMode ? "Save" : "Submit")}
                    disabled={isSubmitting || isUpdating}
                    type="submit"
                  />
                  {isEditMode && (
                    <CustomButton
                      onClick={handlePrint}
                      startIcon={<LocalPrintshopOutlinedIcon />}
                    />
                  )}
                </Box>
              </Box>
            </Box>
          </Form>
        )}
      </Formik>
    </FormLayout>
  );
}
